import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { SearchX, ShieldAlert } from 'lucide-react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CountryProvider } from './contexts/CountryContext';
import { SidebarLayout } from './components/SidebarLayout';
import { LoginPage } from './pages/LoginPage';
import { SKUMasterPage } from './pages/SKUMasterPage';
import { InventoryPage } from './pages/InventoryPage';
import { DailySalesPage } from './pages/DailySalesPage';
import SalesHubPage from './pages/SalesHubPage';
import { RetailPOPage } from './pages/RetailPOPage';
import ShipmentPlanningPage from './pages/ShipmentPlanningPage';
import POSplitPlannerPage from './pages/POSplitPlannerPage';
import ConsolidationPlannerPage from './pages/ConsolidationPlannerPage';
import PODetailPage from './pages/PODetailPage';
import PurchasesPage from './pages/PurchasesPage';
import StockIssuesPage from './pages/StockIssuesPage';
import B2bOrdersPage from './pages/B2bOrdersPage';
import { ReorderPage } from './pages/ReorderPage';
import { StockHealthPage } from './pages/StockHealthPage';
import { PerformanceAlertsPage } from './pages/PerformanceAlertsPage';
import { ReturnsPage } from './pages/ReturnsPage';
import BusinessReviewPage from './pages/BusinessReviewPage';
import { UserManagementPage } from './pages/UserManagementPage';
import CodeMappingsPage from './pages/CodeMappingsPage';
import { TargetsPage } from './pages/TargetsPage';
import { SettingsPage } from './pages/SettingsPage';
import ProfitabilityPage from './pages/ProfitabilityPage';
import AdsPage from './pages/AdsPage';
import IntegrationsPage from './pages/IntegrationsPage';
import CatalogNamesPage from './pages/CatalogNamesPage';
import ChannelsPage from './pages/ChannelsPage';
import { ChannelsProvider } from './hooks/useChannels';
import { Toaster } from './components/ui/sonner';
import { Button } from './components/ui/button';
import '@/App.css';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" replace />;
};

const AdminRoute = ({ children }) => {
  const { user, loading, verified } = useAuth();

  // Admin pages wait for the server to confirm the session: a remembered role could be out of date.
  if (loading || !verified) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-muted-foreground">Loading...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return user.role === 'admin' ? children : (
    <SidebarLayout>
      <ShellMessage
        icon={ShieldAlert}
        title="Admins only"
        body="This page is only available to admin users. Ask an admin if you need something changed here."
        testid="admin-only-page"
      />
    </SidebarLayout>
  );
};

// Simple centred message used for "not found" and "no access" views inside the shell.
const ShellMessage = ({ icon: Icon, title, body, testid }) => (
  <div className="flex flex-col items-center justify-center text-center py-16 sm:py-24 px-4" data-testid={testid}>
    <div className="h-12 w-12 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
      <Icon className="h-6 w-6 text-gray-600" aria-hidden="true" />
    </div>
    <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">{title}</h1>
    <p className="mt-2 text-sm text-gray-600 max-w-md">{body}</p>
    <Button asChild className="mt-6 bg-[#007AFF] hover:bg-[#0062cc] text-white" data-testid={`${testid}-home`}>
      <Link to="/dashboard">Go to Sales overview</Link>
    </Button>
  </div>
);

const NotFoundPage = () => {
  const location = useLocation();
  return (
    <ShellMessage
      icon={SearchX}
      title="Page not found"
      body={`There is no page at ${location.pathname}. It may have moved, or the link may be wrong.`}
      testid="not-found-page"
    />
  );
};

const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <SidebarLayout>
              <SalesHubPage />
            </SidebarLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/skus"
        element={
          <ProtectedRoute>
            <SidebarLayout>
              <SKUMasterPage />
            </SidebarLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory"
        element={
          <ProtectedRoute>
            <SidebarLayout>
              <InventoryPage />
            </SidebarLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/daily-sales"
        element={
          <ProtectedRoute>
            <SidebarLayout>
              <DailySalesPage />
            </SidebarLayout>
          </ProtectedRoute>
        }
      />
      {/* Sales records also lives at /sales/records (same page; /daily-sales stays the main URL). */}
      <Route path="/sales/records" element={<ProtectedRoute><SidebarLayout><DailySalesPage /></SidebarLayout></ProtectedRoute>} />
      {/* /sales was a duplicate of /dashboard (same page); keep the URL working as a redirect. */}
      <Route path="/sales" element={<Navigate to="/dashboard" replace />} />
      <Route
        path="/retail-pos"
        element={
          <ProtectedRoute>
            <SidebarLayout>
              <RetailPOPage />
            </SidebarLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/sales/skus" element={<ProtectedRoute><SidebarLayout><SalesHubPage view="sku" /></SidebarLayout></ProtectedRoute>} />
      <Route path="/sales/brands" element={<ProtectedRoute><SidebarLayout><SalesHubPage view="brand" /></SidebarLayout></ProtectedRoute>} />
      <Route path="/sales/platforms" element={<ProtectedRoute><SidebarLayout><SalesHubPage view="platform" /></SidebarLayout></ProtectedRoute>} />
      <Route path="/operations" element={<Navigate to="/dashboard" replace />} />
      <Route
        path="/retail-pos/:poNumber"
        element={
          <ProtectedRoute>
            <SidebarLayout>
              <PODetailPage />
            </SidebarLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/shipment-planning"
        element={
          <ProtectedRoute>
            <SidebarLayout>
              <ShipmentPlanningPage />
            </SidebarLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/shipment-planning/plan/:poNumber"
        element={
          <ProtectedRoute>
            <SidebarLayout>
              <POSplitPlannerPage />
            </SidebarLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/shipment-planning/consolidation/:planId"
        element={
          <ProtectedRoute>
            <SidebarLayout>
              <ConsolidationPlannerPage />
            </SidebarLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/b2b-orders"
        element={
          <ProtectedRoute>
            <SidebarLayout>
              <B2bOrdersPage />
            </SidebarLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/stock-issues"
        element={
          <ProtectedRoute>
            <SidebarLayout>
              <StockIssuesPage />
            </SidebarLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchases"
        element={
          <ProtectedRoute>
            <SidebarLayout>
              <PurchasesPage />
            </SidebarLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reorder"
        element={
          <ProtectedRoute>
            <SidebarLayout>
              <ReorderPage />
            </SidebarLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/allocation" element={<Navigate to="/shipment-planning" replace />} />
      <Route
        path="/stock-health"
        element={
          <ProtectedRoute>
            <SidebarLayout>
              <StockHealthPage />
            </SidebarLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/performance" element={<Navigate to="/sales/skus" replace />} />
      <Route path="/performance/brands" element={<Navigate to="/sales/brands" replace />} />
      <Route path="/performance/categories" element={<Navigate to="/sales/brands" replace />} />
      <Route path="/performance/skus" element={<Navigate to="/sales/skus" replace />} />
      <Route path="/excel" element={<Navigate to="/daily-sales" replace />} />
      <Route
        path="/alerts"
        element={
          <ProtectedRoute>
            <SidebarLayout>
              <PerformanceAlertsPage />
            </SidebarLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/returns"
        element={
          <ProtectedRoute>
            <SidebarLayout>
              <ReturnsPage />
            </SidebarLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/review"
        element={
          <ProtectedRoute>
            <SidebarLayout>
              <BusinessReviewPage />
            </SidebarLayout>
          </ProtectedRoute>
        }
      />
      {/* The AI Intelligence page became the Business Review (with its Ask box). */}
      <Route path="/ai" element={<Navigate to="/review" replace />} />
      <Route
        path="/targets"
        element={
          <ProtectedRoute>
            <SidebarLayout>
              <TargetsPage />
            </SidebarLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/code-mappings"
        element={
          <ProtectedRoute>
            <SidebarLayout>
              <CodeMappingsPage />
            </SidebarLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ads"
        element={
          <ProtectedRoute>
            <SidebarLayout>
              <AdsPage />
            </SidebarLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profitability"
        element={
          <AdminRoute>
            <SidebarLayout>
              <ProfitabilityPage />
            </SidebarLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/settings/users"
        element={
          <AdminRoute>
            <SidebarLayout>
              <UserManagementPage />
            </SidebarLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/settings/integrations"
        element={
          <AdminRoute>
            <SidebarLayout>
              <IntegrationsPage />
            </SidebarLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/settings/channels"
        element={
          <AdminRoute>
            <SidebarLayout>
              <ChannelsPage />
            </SidebarLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/settings/catalog-names"
        element={
          <AdminRoute>
            <SidebarLayout>
              <CatalogNamesPage />
            </SidebarLayout>
          </AdminRoute>
        }
      />
      <Route
        path="/settings/data"
        element={
          <AdminRoute>
            <SidebarLayout>
              <SettingsPage />
            </SidebarLayout>
          </AdminRoute>
        }
      />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/incoming" element={<Navigate to="/purchases" replace />} />
      {/* Old link target for platform revenue details. */}
      <Route path="/analytics" element={<Navigate to="/sales/platforms" replace />} />
      <Route path="*" element={<ProtectedRoute><SidebarLayout><NotFoundPage /></SidebarLayout></ProtectedRoute>} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <CountryProvider>
        <BrowserRouter>
          <ChannelsProvider>
            <AppRoutes />
          </ChannelsProvider>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </CountryProvider>
    </AuthProvider>
  );
}

export default App;